import { ConnectButton } from "app/components/connect-button/connect-button";
import { useAuth, useLogin } from "app/hooks";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useWallet } from "@solana/wallet-adapter-react";

import { useAccount, useDisconnect } from "wagmi";

import { useUserEventMutation } from "store/api/new.api";
import styles from "./header-connect.module.scss";

type Props = {
  onConnect?: () => void;
  title?: string;
  children?: ReactNode;
  requireSolana?: boolean;
};

declare global {
  interface Window {
    twq?: (...args: any[]) => void;
  }
}

const HeaderConnect: FC<Props> = ({
  onConnect,
  title,
  children,
  requireSolana = false,
}) => {
  const [trackEvent] = useUserEventMutation();
  const { address, isConnected } = useAccount();
  const isAuth = useAuth();
  const [eventSent, setEventSent] = useState<boolean>(false);
  const [authAttempted, setAuthAttempted] = useState<boolean>(false);
  const { disconnect } = useDisconnect();
  const { connected: solanaConnected } = useWallet();
  const { loginMutation, solanaWarning, setSolanaWarning } = useLogin();
  const [showSolanaAlert, setShowSolanaAlert] = useState(false);

  // Track addresses that rejected login to prevent infinite loops
  const userRejectedLogin = useRef<Set<string>>(new Set());

  // First effect: Authenticate when wallet connects (only if no valid tokens exist)
  useEffect(() => {
    const authenticate = async () => {
      // Only authenticate if connected, has address, NOT already authenticated, and haven't attempted yet
      if (isConnected && address && !isAuth && !authAttempted) {
        // Check if user previously rejected login for this address
        if (userRejectedLogin.current.has(address.toLowerCase())) {
          console.log('[HeaderConnect] User previously rejected login for', address);
          return;
        }

        // Double check we don't already have valid tokens in storage
        const hasTokens = Boolean(localStorage.getItem("authTokens"));

        if (!hasTokens) {
          setAuthAttempted(true);

          try {
            const success = await loginMutation(address, requireSolana);
            if (success) {
              // Remove from rejected list if login succeeds
              userRejectedLogin.current.delete(address.toLowerCase());
            } else {
              console.warn('[HeaderConnect] Login failed for', address);
              // Reset if authentication failed so user can try again
              setAuthAttempted(false);
              // Mark as rejected to prevent automatic retries
              userRejectedLogin.current.add(address.toLowerCase());
            }
          } catch (err: any) {
            console.error("[HeaderConnect] Login error:", err);

            // Check if user rejected the request
            const isUserRejection = err?.message?.includes('User rejected') ||
                                   err?.message?.includes('user rejected') ||
                                   err?.code === 4001 ||
                                   err?.name === 'UserRejectedRequestError';

            if (isUserRejection) {
              console.log('[HeaderConnect] User rejected login request, will not retry');
              userRejectedLogin.current.add(address.toLowerCase());
            }

            setAuthAttempted(false);
          }
        }
      } else if (!isConnected || !address) {
        // Reset when wallet disconnects
        setAuthAttempted(false);
        setEventSent(false);
      }
    };

    authenticate();
  }, [isConnected, address, isAuth, authAttempted, loginMutation, requireSolana]);

  // Second effect: Send event after successful authentication
  useEffect(() => {
    if (isConnected && address && isAuth && !eventSent) {
      setEventSent(true);
      trackEvent({ event: "Login", ethAddress: address })
        .unwrap()
        .then(() => {
          if (onConnect) {
            onConnect();
          }
        })
        .catch((error) => {
          console.error("Event tracking failed:", error);
          // Don't reset eventSent - not critical if event tracking fails
        });
    } else if (!isConnected || !address || !isAuth) {
      // Reset event sent flag when user disconnects or logs out
      setEventSent(false);
    }
  }, [isConnected, address, isAuth, trackEvent, onConnect, eventSent]);

  const sendConnectEvent = () => {
    if (!eventSent && typeof window !== "undefined" && window.twq) {
      window.twq("event", "tw-ona64", {});
      setEventSent(true);
    } else {
      console.error("Twitter Pixel is not initialized or event already sent.");
    }
  };

  const handleClick = async () => {
    // If wallet is connected but not authenticated, check for Solana requirement
    if (isConnected && address && !isAuth && requireSolana) {
      const success = await loginMutation(address, true);
      if (!success && solanaWarning) {
        setShowSolanaAlert(true);
        setTimeout(() => setShowSolanaAlert(false), 5000); // Auto-hide after 5 seconds
        return;
      }
    } else {
      disconnect();
    }

    if (!eventSent) {
      sendConnectEvent();
    }
  };

  return (
    <>
      {!isAuth ? (
        <>
          <div
            className={title === "login" ? styles.landing : styles.btn}
            onClick={handleClick}
          >
            <ConnectButton />
          </div>
          {showSolanaAlert && solanaWarning && (
            <div className={styles.solanaAlert}>
              <div className={styles.alertContent}>
                <span className={styles.alertIcon}>⚠️</span>
                <span className={styles.alertText}>{solanaWarning}</span>
                <button
                  className={styles.alertClose}
                  onClick={() => {
                    setShowSolanaAlert(false);
                    setSolanaWarning(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>{children}</>
      )}
    </>
  );
};

export default HeaderConnect;
